'use client';
import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

interface RequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  expert: { pseudonym: string; hourlyRate: number };
}

const ENGAGEMENT_OPTIONS = [
  'Advisory',
  'Retainer',
  'Strategic Project',
  'M&A Due Diligence',
  'Board Advisory',
  'Mentorship',
  'Interim Leadership',
  'Market Entry',
  'Fundraising Support',
  'Operational Turnaround',
  'Other (specify below)',
];

const ASK_MAX = 1000;
const ASK_MIN = 50;
const SCOPE_MAX = 300;

function buildPreFill(model: string): string {
  const token = model && model !== 'Other (specify below)' ? model : '{engagement type}';
  return `We are trying to find a/an ${token} expert who has the experience of multiple such assignments or hands-on practitioner's experience.\n\nIt would be great if you elaborate on your specific and relevant experience in this area with examples. You may keep your responses anonymised — however, more information will help our decision to engage.\n\nLook forward to formalising the engagement and taking it forward.`;
}

export default function RequestModal({ isOpen, onClose, expert }: RequestModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Field 1: Engagement model
  const [engagementModel, setEngagementModel] = useState('');
  const [otherText, setOtherText] = useState('');

  // Field 2: Narrative ask
  const [askText, setAskText] = useState(buildPreFill(''));

  // Optional scope note
  const [scopeNote, setScopeNote] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<{ model?: string; ask?: string; other?: string }>({});

  const isOther = engagementModel === 'Other (specify below)';
  const effectiveModel = isOther ? otherText.trim() : engagementModel;

  // Update pre-fill token when engagement model changes
  useEffect(() => {
    if (!isOpen) return;
    setAskText((prev) => {
      // Replace any existing token pattern or the literal pre-fill model reference
      // We rebuild from scratch only if the text still matches the pre-fill pattern
      const currentPreFill = buildPreFill('');
      const isStillDefault =
        prev === buildPreFill('') ||
        prev === buildPreFill(engagementModel) ||
        ENGAGEMENT_OPTIONS.some((opt) => prev === buildPreFill(opt)) ||
        prev === buildPreFill('{engagement type}');

      if (isStillDefault) {
        return buildPreFill(isOther ? (otherText.trim() || '{engagement type}') : engagementModel);
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementModel, otherText, isOpen]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setEngagementModel('');
      setOtherText('');
      setAskText(buildPreFill(''));
      setScopeNote('');
      setErrors({});
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: { model?: string; ask?: string; other?: string } = {};

    if (!engagementModel) {
      newErrors.model = 'Please select an engagement type.';
    } else if (isOther && !otherText.trim()) {
      newErrors.other = 'Please describe the engagement type.';
    }

    if (!askText.trim()) {
      newErrors.ask = 'Please write your message to the expert.';
    } else if (askText.trim().length < ASK_MIN) {
      newErrors.ask = `Your message must be at least ${ASK_MIN} characters.`;
    } else if (askText.length > ASK_MAX) {
      newErrors.ask = `Your message must not exceed ${ASK_MAX} characters.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Map two fields to the existing Edge Function payload
      const payload = {
        buyer_question_experience: askText.trim(),          // full narrative ask
        buyer_question_relevance: effectiveModel,           // engagement model
        buyer_question_depth: '',
        buyer_question_clarity: '',
        buyer_question_rate_expectation: '',
        buyer_scope_note: scopeNote.trim(),
      };

      // Backend integration: POST to send-intro-request Edge Function
      // await supabase.functions.invoke('send-intro-request', { body: { expert_pseudonym: expert.pseudonym, ...payload } });
      await new Promise((r) => setTimeout(r, 1200));

      setSubmitted(true);
      toast.success(`Enquiry sent to ${expert.pseudonym}`);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Ask your questions"
    >
      <div
        className="surface-elevated max-w-lg w-full p-6 animate-slide-up max-h-[90vh] overflow-y-auto scrollbar-thin"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-heading text-xl text-foreground mb-0.5">Ask Your Questions</h2>
            <p className="text-xs font-mono-data text-muted-foreground">{expert.pseudonym}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
              <CheckCircle size={24} className="text-secondary" />
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Enquiry Sent</p>
              <p className="text-sm text-muted-foreground">
                {expert.pseudonym} will review your enquiry and respond within 24 hours.
                You will be notified when they reply.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">

            {/* Field 1: Engagement Model */}
            <div>
              <label
                className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5"
                htmlFor="engagementModel"
              >
                Type of Engagement <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  id="engagementModel"
                  value={engagementModel}
                  onChange={(e) => {
                    setEngagementModel(e.target.value);
                    setErrors((prev) => ({ ...prev, model: undefined, other: undefined }));
                  }}
                  className="w-full appearance-none bg-input border border-border rounded-lg px-3 py-2.5 pr-9 text-sm text-foreground outline-none focus:border-primary/50 transition-colors cursor-pointer"
                  aria-describedby="engagementModel-error"
                >
                  <option value="" disabled>Select engagement type...</option>
                  {ENGAGEMENT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
              </div>
              {errors.model && (
                <p id="engagementModel-error" className="text-xs text-red-400 mt-1">{errors.model}</p>
              )}

              {/* Other free text */}
              {isOther && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => {
                      if (e.target.value.length <= 100) {
                        setOtherText(e.target.value);
                        setErrors((prev) => ({ ...prev, other: undefined }));
                      }
                    }}
                    maxLength={100}
                    placeholder="Describe the engagement type"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors"
                    aria-describedby="otherText-error"
                  />
                  <div className="flex justify-between mt-1">
                    {errors.other
                      ? <p id="otherText-error" className="text-xs text-red-400">{errors.other}</p>
                      : <span />}
                    <span className="text-xs text-muted-foreground">{otherText.length}/100</span>
                  </div>
                </div>
              )}
            </div>

            {/* Field 2: Narrative Ask */}
            <div>
              <label
                className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5"
                htmlFor="askText"
              >
                Your message to the expert <span className="text-red-400">*</span>
              </label>
              <textarea
                id="askText"
                value={askText}
                onChange={(e) => {
                  if (e.target.value.length <= ASK_MAX) {
                    setAskText(e.target.value);
                    setErrors((prev) => ({ ...prev, ask: undefined }));
                  }
                }}
                rows={8}
                maxLength={ASK_MAX}
                className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors resize-none"
                aria-describedby="askText-error"
              />
              <div className="flex justify-between mt-1">
                {errors.ask
                  ? <p id="askText-error" className="text-xs text-red-400">{errors.ask}</p>
                  : <span />}
                <span className={`text-xs ${askText.length > ASK_MAX * 0.9 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                  {askText.length}/{ASK_MAX}
                </span>
              </div>
            </div>

            {/* Optional: Scope Note */}
            <div>
              <label
                className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5"
                htmlFor="scopeNote"
              >
                Any context for the expert? <span className="text-muted-foreground font-normal normal-case">(optional)</span>
              </label>
              <textarea
                id="scopeNote"
                value={scopeNote}
                onChange={(e) => {
                  if (e.target.value.length <= SCOPE_MAX) setScopeNote(e.target.value);
                }}
                rows={3}
                maxLength={SCOPE_MAX}
                placeholder="Brief background on your situation, company, or what you're trying to solve."
                className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors resize-none"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-muted-foreground">{scopeNote.length}/{SCOPE_MAX}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 py-2.5 px-4 rounded-lg border border-border text-muted-foreground text-sm hover:border-primary/30 hover:text-foreground active:scale-95 transition-all duration-150"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2.5 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 size={14} className="animate-spin" />Sending…</>
                ) : (
                  'Send Enquiry'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}